import type { AnalyticsClientInstance } from "../client";
import { ANALYTICS_EVENTS } from "../events";

/**
 * A single feature-flag evaluation, as surfaced by an OpenFeature evaluation
 * hook. Kept structurally compatible with `@spiriidk/feature-flags`'
 * `FlagEvaluationEvent` so the two packages stay decoupled (no import either way).
 */
interface FlagEvaluationEvent {
  flagKey: string;
  value: boolean | string;
  variant?: string;
  reason?: string;
}

interface FlagEvaluationTrackerOptions {
  /** When false the returned sink is a no-op (e.g. Mixpanel disabled locally). */
  enabled?: boolean;
}

/**
 * Build a deduplicated sink for feature-flag evaluations. For each new
 * `flag:variant` exposure it:
 *  1. registers the flag state as a Mixpanel super property keyed by the flag key
 *     (e.g. `release.connect.charge-map`), so every event can be segmented by it, and
 *  2. fires `$experiment_started` (deduped per session) to populate Mixpanel's
 *     Experiments report.
 *
 * Wire the returned function to `@spiriidk/feature-flags`' `onEvaluation` option.
 *
 * @example
 * initializeFeatureFlags({
 *   clientSideId,
 *   onEvaluation: createFlagEvaluationTracker(analyticsClient, { enabled }),
 * });
 */
function createFlagEvaluationTracker(
  client: AnalyticsClientInstance,
  options: FlagEvaluationTrackerOptions = {}
): (event: FlagEvaluationEvent) => void {
  const { enabled = true } = options;
  const lastVariantByFlag = new Map<string, string>();

  return ({ flagKey, value }) => {
    if (!enabled || !flagKey) {
      return;
    }

    const variant = String(value);
    // Dedupe: only act on the first exposure to a given flag:variant per session.
    if (lastVariantByFlag.get(flagKey) === variant) {
      return;
    }
    lastVariantByFlag.set(flagKey, variant);

    // Attach current flag state to every subsequent event for segmentation.
    // The flag key is already namespaced (release.*/experiment.*), so no prefix.
    client.plugins.mixpanel.setSuperProperties({
      [flagKey]: variant,
    });

    // Power Mixpanel's Experiments report.
    void client.track(ANALYTICS_EVENTS.FEATURE_FLAG_EXPOSURE, {
      "Experiment name": flagKey,
      "Variant name": variant,
    });
  };
}

export { createFlagEvaluationTracker };
export type { FlagEvaluationEvent, FlagEvaluationTrackerOptions };
